import React, { type ReactNode } from 'react';
import { Modal } from '@wfp/ui';


export interface ConfirmOptions {
	title: string;
	message: ReactNode;
	okLabel: string;
	cancelLabel: string;  // pass empty string to hide cancel
	type: 'info' | 'warning' | 'danger';
	wide: boolean;
}

interface Props {
}

interface State {
	open: boolean;
	options?: ConfirmOptions;
	onClose?: (ok: boolean) => void;
}

const defaultOptions: ConfirmOptions = {
	title: 'Confirm',
	message: 'Do you want to proceed?',
	okLabel: 'Ok',
	cancelLabel: '',
	type: 'info',
	wide: false,
};

let currentContainer: ConfirmContainer | undefined;

export class ConfirmContainer extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			open: false,
			options: defaultOptions,
			onClose: undefined,
		};
	}

	componentDidMount() {
		if (currentContainer !== undefined) {
			throw new Error('Cannot mount more than one ConfirmContainer at the same time');
		} else {
			currentContainer = this;
		}
	}

	componentWillUnmount() {
		currentContainer = undefined;
	}

	show(options: Partial<ConfirmOptions>, onClose: (ok: boolean) => void) {
		if (this.state.open) {
			throw new Error('Cannot show a confirm dialog when another one is open.\n' +
				'Current open confirm: ' + JSON.stringify(this.state.options) + '.\n' +
				'New one:' + JSON.stringify(options));
		}

		this.setState({
			open: true,
			options: {...defaultOptions, ...options},
			onClose: onClose,
		});
	}

	close(ok: boolean) {
		const onClose = this.state.onClose!;
		// close the modal first then call the onClose callback (so that the callback is allowed to open another modal)
		this.setState({
			open: false,
			onClose: undefined,
		}, () => onClose(ok));
	}

	render() {
		const s = this.state;
		const o = s.options!;
		const hasCancel = !!o.cancelLabel;  // if there is no cancel button click outside means Ok


		return (
			<Modal
				open={s.open}
				modalHeading={o.title}
				primaryButtonText={o.okLabel}
				secondaryButtonText={o.cancelLabel}
				onRequestSubmit={() => this.close(true)}
				onSecondarySubmit={() => this.close(false)}

				hideClose={hasCancel}
				onRequestClose={() => !hasCancel && this.close(true)}

				type={o.type}
				danger={o.type === 'danger'}
				wide={o.wide}
			>
				<p className="wfp--modal-content__text">
					{o.message}
				</p>
			</Modal>
		);
	}
}


function confirm(options: Partial<ConfirmOptions>): Promise<boolean> {
	if (!currentContainer) {
		throw new Error('No ConfirmContainer mounted yet. Add one in your App render.');
	}

	return new Promise<boolean>((resolve) => {
		currentContainer!.show(options, ok => resolve(ok));
	});
}

export default confirm;
